<template>
  <div
    class="card"
    :class="classes"
    @mouseenter="setImageHover(true)"
    @mouseleave="setImageHover(false)"
  >
    <div class="card__media">
      <a
        :href="product.onlineStoreUrl"
        class="media-anchor"
        @click="handleProductClick"
      >
        <ProductCardMedia
          :hover="hover"
          :primary-media="media"
          :secondary-media="hoverMedia"
        />

        <div class="badges">
          <Badge v-for="badge in badges" :key="badge" theme="product">{{
            badge
          }}</Badge>
        </div>

        <div v-if="hoverText" class="hover">
          <span class="link">{{ hoverText }}</span>
        </div>
      </a>
      <WishlistButton
        v-if="wishlistVariant"
        :product="product"
        :variant="wishlistVariant"
        overlay
        class="card__wishlist"
      />
    </div>

    <div class="info">
      <div class="info__title-row">
        <p class="info__title">{{ product.title }}</p>
        <ColourFamilySwatches
          v-if="!hideColourSwatches"
          :product="product"
          variant="card"
          class="info__swatches info__swatches--desktop"
        />
      </div>
      <ProductCardPrice :product="product" class="price" />
      <ColourFamilySwatches
        v-if="!hideColourSwatches"
        :product="product"
        variant="card"
        class="info__swatches info__swatches--mobile"
      />
      <template v-if="showAddToBag">
        <Link
          tag="button"
          class="add-to-bag-btn"
          @mouseenter="prefetchQuickLookImages"
          @touchstart.passive="prefetchQuickLookImages"
          @click="handleAddToBagClick"
        >
          <span v-if="productCardVariant === 'journal'" aria-hidden="true"
            >+</span
          >
          <span v-else-if="productCardVariant === 'bag'">ADD</span>
          <span v-else-if="productCardVariant === 'edit'">Add to Bag</span>
          <span v-else>Add To Bag</span>
        </Link>
      </template>
    </div>
  </div>
</template>

<script>
import Link from "@/components/Link.vue";
import ProductCardMedia from "@/components/product-card/ProductCardMedia.vue";
import ProductCardPrice from "@/components/product-card/ProductCardPrice.vue";
import ColourFamilySwatches from "@/components/product-display/ColourFamilySwatches.vue";
import Badge from "@/components/BadgeV2.vue";
import WishlistButton from "@/components/WishlistButtonV3.vue";
import { mapActions } from "vuex";
import { productClickEvent } from "@/helpers/events.js";
import { getProductBadges } from "@/helpers/productBadges.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { unwrapNodes } from "@/helpers/graphql";
import { prefetchMiniProductImages } from "@/helpers/miniProductImages.js";

export default {
  name: "ProductCard",
  components: {
    ProductCardPrice,
    Link,
    ProductCardMedia,
    ColourFamilySwatches,
    Badge,
    WishlistButton,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    media: {
      type: Object,
      default: () => {},
    },
    hoverMedia: {
      type: Object,
      default: () => {},
    },
    textAlignment: {
      type: String,
      default: "left",
      validators: [(val) => ["center", "left"].includes(val)],
    },
    hoverText: {
      type: String,
      default: null,
    },
    showAddToBag: {
      type: Boolean,
      default: true,
    },
    horizontalLayout: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Number,
      default: null,
    },
    listName: {
      type: String,
      default: null,
    },
    productCardVariant: {
      type: String,
      default: "default",
      validator: (val) => ["default", "journal", "bag", "edit"].includes(val),
    },
    hideColourSwatches: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["productClick"],
  data() {
    return {
      hover: false,
      quickLookPrefetched: false,
    };
  },
  computed: {
    classes() {
      return {
        "card--center":
          this.textAlignment === "center" ||
          this.productCardVariant === "journal",
        "card--horizontal": this.horizontalLayout,
        "card--journal": this.productCardVariant === "journal",
        "card--bag": this.productCardVariant === "bag",
        "card--edit": this.productCardVariant === "edit",
      };
    },
    badges() {
      if (this.productCardVariant === "bag") {
        return [];
      }

      return getProductBadges(this.product.tags);
    },
    showColourSwatches() {
      if (this.hideColourSwatches) return false;
      const { colourSwatchesEnabled = false } = getFeatureToggles();
      return colourSwatchesEnabled;
    },
    wishlistVariant() {
      const variants = unwrapNodes(this.product?.variants?.edges) || [];
      return (
        variants.find((variant) => variant.availableForSale) ||
        variants[0] ||
        null
      );
    },
  },
  methods: {
    ...mapActions("product", ["openProductQuickLook"]),
    setImageHover(isHovering) {
      const canHover = window.matchMedia(
        "(hover: hover) and (pointer: fine)"
      ).matches;

      if (!canHover) {
        this.hover = false;
        return;
      }

      this.hover = isHovering;
    },
    handleProductClick() {
      productClickEvent({
        product: this.product,
        position: this.position,
        listName: this.listName,
      });
      this.$emit("productClick", this.product);
    },
    // Warm the slide-in carousel while the pointer is still on the button so
    // its images are in cache by the time the panel opens
    prefetchQuickLookImages() {
      if (this.quickLookPrefetched) return;
      this.quickLookPrefetched = true;
      prefetchMiniProductImages(this.product);
    },
    handleAddToBagClick(event) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      this.openProductQuickLook(this.product);
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  text-decoration: none;
}

.card__media {
  position: relative;
  z-index: 0;
  isolation: isolate;
  margin-bottom: 12px;
}

.card__wishlist {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;

  @include respond-above(md) {
    top: 12px;
    right: 12px;
  }
}

.info {
  text-align: left;
  @include utility-label(15px);

  .card--center & {
    text-align: center;
  }
}

.info__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .card--center & {
    flex-direction: column;
    align-items: center;
  }
}

.info__title {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
}

.info__swatches {
  &--desktop {
    @include respond-below(md) {
      display: none;
    }
  }

  &--mobile {
    display: none;

    @include respond-below(md) {
      display: flex;
      // margin-top: 8px;
    }
  }
}

.media-anchor {
  display: block;
  aspect-ratio: 4/5;
  object-fit: cover;
  max-width: 100%;
  background-color: $color-grey-light;
  position: relative;

  &:hover {
    .hover {
      @include respond-above(md) {
        display: block;
      }
    }
  }

  .hover {
    display: none;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);

    .link {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      @include utility-button(13px);
      display: inline-block;
      color: $color-white;
      border-bottom: 1px solid $color-white;
      padding: 4px 0;
      text-transform: uppercase;
    }
  }
}

.price {
  margin-top: 0px;
}

:deep(.media-anchor img),
:deep(.media-anchor video) {
  display: block;
  aspect-ratio: 4/5;
  object-fit: cover;
  max-width: 100%;
}

// .add-to-bag-btn {
//   margin-top: 12px;
// }

.card--journal {
  .add-to-bag-btn {
    margin: 16px auto 0;
    width: 52px;
    height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $color-black;
    color: $color-white;
    border-radius: 4px;
    line-height: 1;
    text-decoration: none;
    font-size: 24px;
  }
}

.card--edit {
  .info {
    @include editorial-body(14px);
    line-height: 1.5;
  }

  :deep(.price) {
    @include editorial-body(14px);
    line-height: 1.5;
    margin-top: 0;
  }

  .add-to-bag-btn {
    @include editorial-body(14px);
    line-height: 1.6;
    text-transform: none;
    margin-top: 8px;
    padding: 0;
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-color: $color-black;
    text-decoration-thickness: 6%;
    text-underline-offset: 35%;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    &::after {
      content: none;
    }
  }
}

.card--bag {
  .add-to-bag-btn {
    margin-top: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: $color-black;
    color: $color-white;
    border-radius: 4px;
    padding: 7px 16px;
    text-decoration: none;
    @include utility-button(13px);

    &::after {
      display: none;
    }
  }
}

.badges {
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @include respond-above(md) {
    top: 12px;
    left: 12px;
  }
}

.card--horizontal {
  display: flex;
  border-bottom: 1px solid #e4e4e4;
  padding: 16px 0;
  gap: 20px;

  .media-anchor {
    flex: 0 0 80px;
    margin-bottom: 0;
  }

  .card__media {
    flex: 0 0 80px;
    margin-bottom: 0;
  }

  .info {
    flex: 1 0 auto;
  }
}
</style>
